import type { ReactNode } from 'react';
import { GovFormWeb } from '@gov-design-system-ce/react-form';
import type { ExtraConfig } from '@gov-nx/core/app';
import {
	useAuthorizedLabel,
	useTranslationWithNamespace,
} from '@gov-nx/core/service';
import type { PageCode } from '@gov-nx/module/page';
import { useProfilCertificatesContextInstance } from '@gov-nx/module/page';
import {
	Button,
	ButtonGroupLayout,
	ContentLayout,
	DataTable,
	Dialog,
	ErrorStatusBanner,
	FormFileUpload,
	Icon,
	InfoBanner,
	LayoutGap,
	SortArrows,
} from '@gov-nx/ui/web';
import { getFormattedPublisher, toDateReadable } from '@gov-nx/utils/common';
import { SouhlasCheckbox } from '../../Autorizace/SouhlasCheckbox';
import { ProfileContentLayout } from '../profil-ROB/ProfileContentLayout';
import { HistoryModal } from './HistoryModal';
import { ProfilCertificatesSkeleton } from './ProfilCertificatesSkeleton';

type CertificatesViewConfig =
	ExtraConfig['profileRoutes']['certifikatyConfig']['profileCertificatesView'];

export interface CertificatesFormProps extends CertificatesViewConfig {
	navigation: ReactNode;
	code: PageCode;
}

export const ProfileCertificatesView = ({
	navigation,
	code,
	...profileCertificatesViewConfig
}: CertificatesFormProps) => {
	const {
		certificates,
		controls,
		setControls,
		deleteCertificate,
		hasPermission,
		formDefinition,
		isAddButtonDisabled,
		isSubmitting,
		onSubmit,
		sortCertificates,
		history,
		fileUploadHook,
		isLowAuthorization,
		isAuthorizationEnforced,
	} = useProfilCertificatesContextInstance();
	const { getLocalizeCurried } = useTranslationWithNamespace();
	const ts = getLocalizeCurried(code);
	const authLabel = useAuthorizedLabel();
	const submitLabel = authLabel(
		ts('akce.pridat-certifikat'),
		isAuthorizationEnforced
	);

	if (controls.initialLoading) {
		return (
			<ContentLayout customClasses="pt-4!">
				{navigation}
				<ProfilCertificatesSkeleton />
			</ContentLayout>
		);
	}

	if (controls.initialDataError) {
		return (
			<ContentLayout customClasses="pt-4!">
				{navigation}
				<ErrorStatusBanner code={code} />
			</ContentLayout>
		);
	}

	return (
		<div data-testid="profil-certifikaty-view">
			<ProfileContentLayout
				{...profileCertificatesViewConfig.profileContentLayout}>
				{navigation}
				<LayoutGap gap="m">
					{Array.isArray(certificates) && certificates.length === 0 ? (
						<InfoBanner
							headline={ts('stav.chyba.slozka-je-prazdna')}
							text={ts('stav.chyba.nenasli-jsme-zadne-udaje')}
						/>
					) : (
						<div className="gov-table">
							<DataTable responsive>
								<thead>
									<tr>
										<th>
											{ts('zaznam.vydavatel')}
											<SortArrows
												descWcagLabel={`${ts('razeni.title')}: ${ts(
													'zaznam.vydavatel'
												)} - ${ts('razeni.sestupne')}`}
												descOnClick={() =>
													sortCertificates('vydavatel', 'desc')
												}
												ascWcagLabel={`${ts('razeni.title')}: ${ts(
													'zaznam.vydavatel'
												)} - ${ts('razeni.vzestupne')}`}
												ascOnClick={() => sortCertificates('vydavatel', 'asc')}
											/>
										</th>
										<th>
											{ts('zaznam.seriove-cislo')}
											<SortArrows
												descWcagLabel={`${ts('razeni.title')}: ${ts(
													'zaznam.seriove-cislo'
												)} - ${ts('razeni.sestupne')}`}
												descOnClick={() =>
													sortCertificates('serioveCislo', 'desc')
												}
												ascWcagLabel={`${ts('razeni.title')}: ${ts(
													'zaznam.seriove-cislo'
												)} - ${ts('razeni.vzestupne')}`}
												ascOnClick={() =>
													sortCertificates('serioveCislo', 'asc')
												}
											/>
										</th>
										<th>
											{ts('zaznam.platnost')}
											<SortArrows
												descWcagLabel={`${ts('razeni.title')}: ${ts(
													'zaznam.platnost'
												)} - ${ts('razeni.sestupne')}`}
												descOnClick={() =>
													sortCertificates('platnostDo', 'desc')
												}
												ascWcagLabel={`${ts('razeni.title')}: ${ts(
													'zaznam.platnost'
												)} - ${ts('razeni.vzestupne')}`}
												ascOnClick={() => sortCertificates('platnostDo', 'asc')}
											/>
										</th>
										<th className="w-14" />
									</tr>
								</thead>
								<tbody>
									{certificates?.map((cert) => (
										<tr key={cert.serioveCislo}>
											<td>
												<div className="font-bold sm:hidden">
													{ts('zaznam.vydavatel')}:
												</div>
												<div>
													{cert.vydavatel
														? getFormattedPublisher(cert.vydavatel)
														: ''}
												</div>
											</td>
											<td>
												<div className="font-bold sm:hidden">
													{ts('zaznam.seriove-cislo')}:
												</div>
												<div>{cert.serioveCislo}</div>
											</td>
											<td>
												<div className="font-bold sm:hidden">
													{ts('zaznam.platnost')}:
												</div>
												<div>
													{cert.platnostDo
														? toDateReadable(cert.platnostDo)
														: ''}
												</div>
											</td>
											<td>
												<Button
													onClick={() => {
														setControls({
															displayRemoveModal: true,
															selectedCertificate: cert,
														});
													}}
													color="error"
													size="s"
													type="base"
													isDisabled={!hasPermission}
													aria-label={ts('akce.odstranit-certifikat')}>
													<Icon
														name="x-lg"
														type="components"
													/>
												</Button>
											</td>
										</tr>
									))}
								</tbody>
							</DataTable>
						</div>
					)}
					<ButtonGroupLayout>
						<HistoryModal history={history} />
						<Button
							onClick={() => {
								setControls({ displayCertificateAddingModal: true });
							}}
							nativeType="submit"
							color="primary"
							isDisabled={!hasPermission}
							size="l"
							type="solid"
							isExpandedMobile
							aria-label={ts('akce.pridat-certifikat')}>
							{ts('akce.pridat-certifikat')}
						</Button>
					</ButtonGroupLayout>
				</LayoutGap>
			</ProfileContentLayout>

			<Dialog
				isOpen={controls.displayRemoveModal}
				onClose={() => setControls({ displayRemoveModal: false })}
				title={<h3>{ts('certifikaty.odstranit-certifikat')}</h3>}
				footer={
					<>
						<Button
							onClick={() => setControls({ displayRemoveModal: false })}
							color="primary"
							type="base"
							size="m"
							aria-label={ts('akce.zrusit')}>
							{ts('akce.zrusit')}
						</Button>
						<Button
							onClick={() => {
								setControls({
									initialLoading: true,
									displayRemoveModal: false,
								});
								deleteCertificate(controls.selectedCertificate);
							}}
							color="error"
							type="solid"
							size="m"
							aria-label={ts('akce.odstranit')}>
							{ts('akce.odstranit')}
						</Button>
					</>
				}>
				<p
					dangerouslySetInnerHTML={{
						__html: ts('certifikaty.chcete-odstranit'),
					}}
				/>
			</Dialog>
			<GovFormWeb
				formDefinition={formDefinition}
				onSubmit={onSubmit}>
				<Dialog
					isOpen={controls.displayCertificateAddingModal}
					onClose={() => {
						setControls({ displayCertificateAddingModal: false });
						formDefinition.formReset();
					}}
					title={<h3>{ts('certifikaty.pridat-certifikat')}</h3>}
					footer={
						<Button
							isDisabled={isAddButtonDisabled}
							isLoading={isSubmitting}
							nativeType="submit"
							color="primary"
							size="l"
							type="solid"
							aria-label={submitLabel}>
							{submitLabel}
						</Button>
					}>
					<LayoutGap gap="l">
						<FormFileUpload
							fileUploadHook={fileUploadHook}
							field={{ name: 'nazevSouboru', expanded: true }}
						/>
						<SouhlasCheckbox
							show={isLowAuthorization}
							disabled={isSubmitting}
						/>
					</LayoutGap>
				</Dialog>
			</GovFormWeb>
		</div>
	);
};
